import React, { Fragment, useState, useEffect, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import AddIcon from '@material-ui/icons/Add';

import { PersonAddIcon } from 'LegacyWeployDesignLanguage/icons';
import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';
import Snackbar from 'LegacyWeployDesignLanguage/Snackbar';
import Grid from 'LegacyWeployDesignLanguage/Grid';

import Button from 'WeployDesignLanguage/buttons/Button';

import DashboardMetrics from '../../../components/weployer/jobs/DashboardMetrics';
import DashboardWeployeesList from '../../../components/weployer/dashboard/DashboardWeployeesList';
import OnboardingModal from '../../../components/weployer/dashboard/OnboardingModal';
import JobTypeSelectionModal from '../jobTypeSelectionModal/JobTypeSelectionModal';
import {
  DASHBOARD_PATH,
  JOBS_TEMPLATES_PATH,
  NEW_INVITATION_PATH,
  EXTEND_JOB_PATH,
  getExternalPath,
  getInternalPath,
} from '../pathHelpers';
import {
  ORGANISATION,
  ME,
} from '../common/graphql/constants';
import {
  GET_JOB_METRIC,
  GET_ACTIVE_AND_UPCOMING_POSITIONS,
} from './graphql/queries';
import {
  trackEvent,
  trackPageView,
  DASHBOARD,
  NOTIFICATION_ONBOARDING_CLOSE,
  NOTIFICATION_ONBOARDING_NEXT_CLICK,
  NOTIFICATION_ONBOARDING_OPEN,
  WEPLOYER,
} from '../../../utils/analytics';

import { PolicyContext, VIEW_ORGANISATION_JOBS } from '../PolicyProvider';

const POLL_INTERVAL = 30000;
const DRAWER_WIDTH = '500px';

const StyledButtonsContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StyledButton = styled(Button)`
  margin-right: ${props => props.theme.space.single};
`;

const StyledDashboardMetrics = styled.div`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StyledWelcomeContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StyledPageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledDashboardContainer = styled.div`
  @media only screen and (min-width: 1224px) {
    width: 1200px;
  }

  @media only screen and (min-width: 2048px) {
    ${props => props.drawerOpen && css`
      margin-left: -${DRAWER_WIDTH};
      transition: margin-left 0.25s linear;
    `};
  }
`;

const StyledAnchor = styled.a`
  ${props => props.theme.typography.button};
  color: ${props => props.theme.colors.text.primary};
  padding-left: ${props => props.theme.spacing[1]};

  &:hover {
    cursor: pointer;
  }
`;

const Dashboard = ({
  applicationConfig: { features: { PAST_WEPLOYEE_JOB_POSTING } },
  jobsPath,
  timesheetsPath,
  navigateExternal,
  history,
}) => {
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [jobTypeSelectionModalVisible, setJobTypeSelectionModalVisible] = useState(false);
  const { role, actions } = useContext(PolicyContext);
  const isPolicyUnavailable = (role === null);

  const canViewOrganisationJobs = () => (
    actions.includes(VIEW_ORGANISATION_JOBS)
  );

  const { loading: jobsMetricLoading, data: jobsMetricData } = useQuery(GET_JOB_METRIC, {
    fetchPolicy: 'network-only',
    variables: {
      ownedBy: canViewOrganisationJobs() ? ORGANISATION : ME,
    },
    pollInterval: POLL_INTERVAL,
    skip: isPolicyUnavailable,
  });

  const { loading: positionsQueryLoading, data: positionsQueryData } = useQuery(GET_ACTIVE_AND_UPCOMING_POSITIONS, {
    fetchPolicy: 'network-only',
    variables: {
      ownedBy: canViewOrganisationJobs() ? ORGANISATION : ME,
    },
    pollInterval: POLL_INTERVAL,
    skip: isPolicyUnavailable,
  });

  const activeJobsCount = get(jobsMetricData, 'weployer.jobMetric.activeJobsCount', 0);
  const timesheetsToApproveCount = get(jobsMetricData, 'weployer.jobMetric.timesheetsToApproveCount', 0);
  const activeWeployeesCount = get(jobsMetricData, 'weployer.jobMetric.activeWeployeesCount', 0);

  const activeAndUpcomingPositions = get(positionsQueryData, 'weployer.activeAndUpcomingPositions', []);

  useEffect(() => trackPageView(WEPLOYER, DASHBOARD), []);

  const handleClick = path => (event) => {
    event.preventDefault();
    navigateExternal(getExternalPath({ pathName: path }));
  };

  const handlePostJobButtonClick = () => {
    if (PAST_WEPLOYEE_JOB_POSTING) {
      setJobTypeSelectionModalVisible(true);
    } else {
      history.push(getInternalPath({ pathName: JOBS_TEMPLATES_PATH }));
    }
  };

  const navigateToExtensionsPage = (jobId, positionId) => {
    const path = getExternalPath({
      pathName: EXTEND_JOB_PATH,
      pathParams: { jobId },
      params: { origin_path: getInternalPath({ pathName: DASHBOARD_PATH }), 'position_ids[]': positionId },
    });
    navigateExternal(path);
  };

  const handleJobsMetricClick = () => {
    history.push(jobsPath);
  };

  const handleWeployeesMetricClick = () => {
    history.push(jobsPath);
  };

  const handleTimesheetsMetricClick = () => {
    history.push(timesheetsPath);
  };

  const handleOnboardingModalOpen = () => {
    setOnboardingModalOpen(true);
    trackEvent(NOTIFICATION_ONBOARDING_OPEN);
  };

  const handleOnboardingModalClose = () => {
    setOnboardingModalOpen(false);
    trackEvent(NOTIFICATION_ONBOARDING_CLOSE);
  };

  const handleJobPostingStrategyModalClose = () => {
    setJobTypeSelectionModalVisible(false);
  };

  return (
    <Fragment>
      <Snackbar
        visible={jobsMetricLoading}
        message="Fetching job metrics..."
      />
      <StyledPageContainer>
        <StyledDashboardContainer class="test">
          <Grid container spacing="2">
            <Grid item xs={12} sm={6}>
              <StyledButtonsContainer>
                <StyledButton
                  startIcon={<AddIcon />}
                  onClick={() => handlePostJobButtonClick()}
                >
                  Post a job
                </StyledButton>
                <StyledButton
                  appearance={Button.TEXT}
                  startIcon={<PersonAddIcon />}
                  onClick={handleClick(NEW_INVITATION_PATH)}
                >
                  Invite a Colleague
                </StyledButton>
              </StyledButtonsContainer>
              <StyledWelcomeContainer>
                <h4>Welcome back!</h4>
                <div>
                  {'Here\'s what\'s been happening in your organisation'}
                  <StyledAnchor onClick={handleOnboardingModalOpen}>
                    Learn more
                  </StyledAnchor>
                </div>
              </StyledWelcomeContainer>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Loader loading={jobsMetricLoading && !jobsMetricData} text="Loading..." >
                <StyledDashboardMetrics>
                  <DashboardMetrics
                    timesheetsToApprove={timesheetsToApproveCount}
                    totalJobs={activeJobsCount}
                    totalWeployees={activeWeployeesCount}
                    onJobsMetricClick={handleJobsMetricClick}
                    onWeployeesMetricClick={handleWeployeesMetricClick}
                    onTimesheetsMetricClick={handleTimesheetsMetricClick}
                  />
                </StyledDashboardMetrics>
              </Loader>
            </Grid>
          </Grid>
          <div>
            <Loader loading={positionsQueryLoading && !positionsQueryData} text="Loading..." >
              <DashboardWeployeesList
                data={activeAndUpcomingPositions}
                onExtendButton={navigateToExtensionsPage}
              />
            </Loader>
          </div>
        </StyledDashboardContainer>
      </StyledPageContainer>
      <OnboardingModal
        onClose={handleOnboardingModalClose}
        onNextClick={() => trackEvent(NOTIFICATION_ONBOARDING_NEXT_CLICK)}
        open={onboardingModalOpen}
      />
      {jobTypeSelectionModalVisible &&
        <JobTypeSelectionModal
          open={jobTypeSelectionModalVisible}
          onJobPostingStrategyModalClose={handleJobPostingStrategyModalClose}
        />
      }
    </Fragment>
  );
};

Dashboard.propTypes = {
  applicationConfig: PropTypes.object.isRequired,
  navigateExternal: PropTypes.func.isRequired,
  jobsPath: PropTypes.string.isRequired,
  timesheetsPath: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(Dashboard);
